import { useMemo, useEffect, useState, useCallback } from 'react';
import {
  CheckboxGroup,
  Icon,
  Button,
  Stack,
  VStack,
  StackSeparator,
  Flex,
  Spacer,
  Box,
  Text,
  Drawer,
  Portal,
  CloseButton,
} from '@chakra-ui/react';
import { PlusIcon } from '@heroicons/react/24/outline';
import QuickSearchForm from '@/components/ui/quick-search-form';
import { Checkbox } from '@/components/ui/checkbox';
import { toaster } from '@/components/ui/toaster';
import api from '@/utils/api';
import { StaticField, Field as IField } from '@/lib/types';
import { FIELD_TYPES } from '@/lib/constants';

interface Field extends IField {
  content_type_id: number;
}

interface ContentTypeField {
  field_id: number;
  field_name: string;
  content_type_id: number;
  workspace_id: number;
  referenceID: string;
  field_unique_name: string;
  field_type: number;
  properties: any;
}

interface Props {
  isOpen: boolean;
  onClose(): void;
  workspaceId: string;
  existingFields: any[];
  contentTypeId: string;
  onSuccess(): void;
}

export default function CreateContentTypeField({
  isOpen,
  onClose,
  workspaceId,
  existingFields,
  contentTypeId,
  onSuccess,
}: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fields, setFields] = useState<Field[]>([]);
  const [searchResults, setSearchResults] = useState<Field[]>([]);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const existingFieldIds = useMemo(() => {
    return new Set(existingFields.map((field) => field.field_id));
  }, [existingFields]);

  const isStaticField = useCallback((fieldUniqueName: string) => {
    return [
      StaticField.Name,
      StaticField.IsPublic,
      StaticField.CreatedDate,
      StaticField.UpdatedDate,
    ].includes(fieldUniqueName as StaticField);
  }, []);

  useEffect(() => {
    if (!isOpen || !workspaceId || !contentTypeId) {
      return;
    }

    async function fetchField() {
      setIsLoading(true);
      setSelectedFields([]);

      try {
        const resContentType = await api.get(`content-types/${contentTypeId}`);
        const contentType = resContentType.data.data;

        if (!contentType.workspace_id) {
          return;
        }
        const baseFieldsResponse = await api.get(
          `content-type-fields/${contentTypeId}/${contentType.workspace_id}/data?section_title_ignore=true`,
        );
        const baseFields = baseFieldsResponse.data.data
          .filter(
            (field: ContentTypeField) =>
              !isStaticField(field.field_unique_name) && !existingFieldIds.has(field.field_id),
          )
          .map((field: ContentTypeField) => ({
            id: field.field_id,
            name: field.field_name,
            content_type_id: field.content_type_id,
            workspace_id: field.workspace_id,
            reference_id: field.referenceID,
            unique_name: field.field_unique_name,
            field_type: field.field_type,
            properties: field.properties,
            created_at: '',
            updated_at: '',
          }));

        const workspaceFieldsResponse = await api.get(
          `fields/workspace/${workspaceId}?ignore_section_field_type=true`,
        );

        const fields = workspaceFieldsResponse.data.data.filter(
          (field: Field) =>
            !isStaticField(field.unique_name) &&
            !existingFieldIds.has(field.id) &&
            [parseInt(contentTypeId, 10), 0].includes(field.content_type_id),
        );

        const mergedFields = [
          ...baseFields,
          ...fields.filter(
            (field: Field) => !baseFields.some((baseField: Field) => baseField.id === field.id),
          ),
        ];

        setSearchResults(mergedFields);
        setFields(mergedFields);
      } catch (err: any) {
        toaster.create({
          type: 'error',
          title: err.response?.data?.error || 'Failed to fetch field',
        });
      } finally {
        setIsLoading(false);
      }
    }

    fetchField();
  }, [workspaceId, contentTypeId, existingFieldIds, isOpen, isStaticField]);

  const handleSearch = useCallback(
    (search: string) => {
      const query = search.toLowerCase().trim();

      if (!query) {
        setSearchResults(fields);
        return;
      }

      const filtered = fields.filter((field) => field.name.toLowerCase().includes(query));

      setSearchResults(filtered);
    },
    [fields],
  );

  async function onSubmit() {
    try {
      setIsSubmitting(true);
      selectedFields.forEach(async (value) => {
        await api.post(`content-type-fields`, {
          content_type_id: parseInt(contentTypeId),
          workspace_id: parseInt(workspaceId),
          field_id: parseInt(value),
          section_id: null,
        });
      });
      onClose();
      onSuccess();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to create field',
      });
    } finally {
      setIsSubmitting(false);
      setSelectedFields([]);
    }
  }

  function handleChange(values: string[]) {
    setSelectedFields(values);
  }

  function getFieldName(fieldType: number) {
    return FIELD_TYPES.find((field) => field.id === fieldType)?.name;
  }

  return (
    <Drawer.Root
      id='select-field-drawer'
      size='lg'
      placement='end'
      open={isOpen}
      onOpenChange={onClose}
    >
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.CloseTrigger asChild>
              <CloseButton size='sm' />
            </Drawer.CloseTrigger>
            <Drawer.Header>
              <Drawer.Title>Add a field</Drawer.Title>
            </Drawer.Header>
            <Drawer.Body>
              <Flex mb={4}>
                <Spacer />
                <Button
                  colorPalette='brand'
                  loading={isSubmitting}
                  disabled={selectedFields.length === 0}
                  onClick={onSubmit}
                >
                  <Icon as={PlusIcon} w={4} h={4} />
                  Add Selected
                </Button>
              </Flex>
              <Stack mb={8}>
                <QuickSearchForm handleSearch={handleSearch} />
              </Stack>
              {isLoading ? (
                <Text textAlign='center' color='gray.500' py={4}>
                  Loading fields...
                </Text>
              ) : searchResults.length === 0 ? (
                <Text textAlign='center' color='gray.500' py={4}>
                  No available fields found. All fields have already been added or no fields match
                  your search.
                </Text>
              ) : (
                <CheckboxGroup
                  colorPalette='brand'
                  defaultValue={selectedFields}
                  onValueChange={handleChange}
                >
                  <VStack
                    align={'stretch'}
                    gap={3}
                    separator={<StackSeparator borderColor='gray.200' />}
                  >
                    {searchResults.map((field) => (
                      <Checkbox key={field.id} value={field.id.toString()}>
                        <Stack gap={1}>
                          <Box lineHeight='1'>{field.name}</Box>
                          <Box fontWeight='normal' fontSize={'xs'} color='fg.muted'>
                            <Box fontWeight={'bold'} display={'inline-block'}>
                              {getFieldName(field.field_type)}
                            </Box>{' '}
                            field
                          </Box>
                        </Stack>
                      </Checkbox>
                    ))}
                  </VStack>
                </CheckboxGroup>
              )}
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
}
